<template>
  <teleport to="body">
    <div class="container-fluid bb-popup-outer">
      <div class="bb-popup-bg d-flex justify-content-center align-items-center">
        <div
          class="card shadow bb-card bb-popup-card mb-2 rounded bb-popup-table-card"
        >
          <div
            class="container-fluid p-4 bb-tooltip-wrapper bb-popup-table-body"
          >
            <div class="bb-popup-table-controls mb-4">
              <div class="row align-items-center flex-grow-1 g-3">
                <div class="col">
                  <div class="bb-page-title">Combat Configuration</div>
                </div>
                <div class="col-auto">
                  <div class="bb-popup-table-actions">
                    <button
                      type="button"
                      class="btn btn-lg btn-primary"
                      @click="handleClose"
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="errorMessages.length > 0">
              <div class="row mt-2 mb-3">
                <div class="form-group">
                  <div class="row">
                    <div class="col text-end me-3">
                      <div
                        v-for="errorMessage in errorMessages"
                        :key="errorMessage"
                        class="bb-error-wrap"
                        style="white-space: pre-line"
                      >
                        <div class="row mb-3">
                          {{ errorMessage }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="isLoading"
              class="bb-loader bb-popup-table-loader"
            ></div>

            <div
              v-else-if="configRows.length === 0"
              class="mt-2 mb-3"
            >
              <div class="form-group">
                <div class="row mx-0">
                  <div class="col text-end me-3">
                    <div
                      class="bb-error-wrap"
                      style="white-space: pre-line"
                    >
                      <div class="row mb-3">
                        {{ emptyMessage }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="bb-popup-table-table-wrapper"
            >
              <div class="table-responsive">
                <table class="table table-borderless align-middle mb-0">
                  <tbody>
                    <tr
                      v-for="configRow in configRows"
                      :key="configRow.name"
                    >
                      <td class="bb-combat-config-setting-col">
                        <div class="form-floating">
                          <input
                            :id="`combat-config-name-${configRow.name}`"
                            readonly
                            disabled
                            class="form-control"
                            :value="configRow.verbose_name"
                          />
                          <label
                            class="bb-float-label optional"
                            :for="`combat-config-name-${configRow.name}`"
                          >
                            Setting
                          </label>
                        </div>
                      </td>
                      <td class="bb-combat-config-value-col">
                        <div class="bb-combat-config-value-cell">
                          <img
                            :src="configRow.value ? tickImg : cancelImg"
                            :class="[
                              'bb-combat-config-value-image',
                              configRow.value
                                ? 'bb-combat-config-value-image-true'
                                : 'bb-combat-config-value-image-false',
                            ]"
                            :alt="configRow.value ? 'True' : 'False'"
                            height="42"
                            width="42"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { axios } from '@/common/api.service.js'
import cancelImg from '../assets/images/bbCancel.png'
import tickImg from '../assets/images/bbTick.png'
import { mapGetters } from 'vuex'

export default {
  name: 'CombatConfigView',
  data() {
    return {
      cancelImg,
      isLoading: false,
      errorMessages: [],
      configRows: [],
      previousBodyOverflow: '',
      previousHtmlOverflow: '',
      tickImg,
    }
  },
  computed: {
    ...mapGetters('auth', {
      getHeaders: 'getHeaders',
    }),
    emptyMessage() {
      return 'No app settings booleans are available.'
    },
  },
  mounted() {
    this.lockBackgroundScroll()
    this.fetchConfigRows()
  },
  beforeUnmount() {
    this.restoreBackgroundScroll()
  },
  methods: {
    lockBackgroundScroll() {
      this.previousBodyOverflow = document.body.style.overflow || ''
      this.previousHtmlOverflow = document.documentElement.style.overflow || ''
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    },
    restoreBackgroundScroll() {
      document.body.style.overflow = this.previousBodyOverflow
      document.documentElement.style.overflow = this.previousHtmlOverflow
    },
    extractErrorMessage(error) {
      const payload = error?.response?.data

      if (typeof payload?.detail === 'string' && payload.detail) {
        return payload.detail
      }

      return 'Unable to load combat configuration.'
    },
    async fetchConfigRows() {
      this.isLoading = true
      this.errorMessages = []

      try {
        const response = await axios.get(
          'api/v1/campaigns/settings/boolean-fields/',
          {
            headers: this.getHeaders || {},
            mode: 'same-origin',
          },
        )

        if (!Array.isArray(response?.data)) {
          throw new Error('Unexpected app settings response.')
        }

        this.configRows = response.data
      } catch (error) {
        this.configRows = []
        this.errorMessages = [
          window.bbUiErrorMsg(this.extractErrorMessage(error)),
        ]
      } finally {
        this.isLoading = false
      }
    },
    handleClose() {
      this.closeDialog()
    },
    closeDialog() {
      if (this.$route.name) {
        const query = { ...this.$route.query }
        delete query.popup

        if (
          this.$route.name === 'myCharactersView' ||
          this.$route.name === 'combatCharacters'
        ) {
          delete query.slug
        }

        this.$router.replace({
          name: this.$route.name,
          params: { ...this.$route.params },
          query,
        })
        return
      }

      this.$router.replace({ name: 'campaignsView' })
    },
  },
}
</script>

<style scoped>
.bb-popup-outer {
  position: fixed;
  inset: 0;
  z-index: 4000;
}

.bb-popup-bg {
  min-height: 100vh;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.58);
}

.bb-popup-table-card {
  width: min(700px, 93vw);
  max-height: 92vh;
}

.bb-popup-table-body {
  max-height: 92vh;
  overflow-y: auto;
}

.bb-popup-table-controls {
  display: flex;
  align-items: center;
}

.bb-popup-table-actions {
  display: flex;
  justify-content: flex-end;
}

.bb-popup-table-table-wrapper {
  padding-right: 0.25rem;
}

.bb-popup-table-table-wrapper table {
  table-layout: fixed;
}

.bb-combat-config-setting-col {
  width: 420px;
}

.bb-combat-config-value-col {
  width: 72px;
}

.bb-combat-config-value-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 58px;
}

.bb-combat-config-value-image {
  display: block;
}

.bb-combat-config-value-image-true {
  filter: brightness(0) saturate(100%) invert(39%) sepia(89%) saturate(1270%)
    hue-rotate(83deg) brightness(97%) contrast(87%);
}

.bb-combat-config-value-image-false {
  filter: brightness(0) saturate(100%) invert(19%) sepia(96%) saturate(4460%)
    hue-rotate(350deg) brightness(102%) contrast(95%);
}

@media (max-width: 767.98px) {
  .bb-popup-bg {
    padding: 0.75rem;
  }

  .bb-popup-table-card {
    width: 100%;
    max-height: 96vh;
  }

  .bb-popup-table-body {
    max-height: 96vh;
  }
}
</style>
